/**
 * /plugins/$pluginId — Plugin surface host. Surface Layer (/01)
 *
 * Sandboxed, manifest-driven host for one plugin (resolved from the :pluginId
 * route param). The isolated plugin surface tree mounts inside this scope once
 * its manifest is resolved; the host itself loads no plugin SDK and trusts no
 * plugin code. Outside-in: the container is a clean, error-boundary-friendly
 * shell that renders correctly while the plugin surface is pending.
 */

import { createFileRoute } from '@tanstack/react-router';
import { InlineNotification, SkeletonText, Tag, Tile } from '@carbon/react';
import { Plug } from '@carbon/icons-react';
import { usePluginContracts } from '@lib/persistence/useSurfaceContracts';
import '../styles/views.scss';

export const Route = createFileRoute('/plugins/$pluginId')({
  component: PluginHostView,
});

function PluginHostView() {
  const { pluginId } = Route.useParams();
  const { rows, fromBackend, isLoading } = usePluginContracts();
  const entry = rows?.find((row) => row.plugin_key === pluginId) ?? null;

  return (
    <div className="view">
      <header className="view__header">
        <h1 className="view__title">Plugin · {pluginId}</h1>
        <p className="view__subtitle">
          Isolated host for a single plugin surface. Plugin trees mount inside this scope without access to the workspace shell.
        </p>
      </header>

      <InlineNotification
        kind="info"
        lowContrast
        hideCloseButton
        title="Sandboxed surface"
        subtitle="Plugin surfaces are sandboxed and manifest-driven. The host renders the plugin tree only after its manifest is resolved; it never loads plugin code directly."
      />

      {isLoading && !rows ? (
        <SkeletonText paragraph lineCount={3} />
      ) : entry ? (
        <Tile className="view-tile view__grid">
          <div className="view-tile__row">
            <strong>{entry.plugin_key}</strong>
            <Tag type={entry.status === 'enabled' ? 'green' : 'cool-gray'}>{entry.status}</Tag>
          </div>
          <div className="notification-row__meta">
            <span>spoke: {entry.spoke_key}</span>
            <Tag type={fromBackend ? 'teal' : 'cool-gray'}>{fromBackend ? 'live catalog' : 'fallback'}</Tag>
          </div>
          <p className="view-tile__label">
            Manifest resolved from the plugin catalog. The isolated surface tree mounts here under this plugin scope.
          </p>
        </Tile>
      ) : (
        <Tile className="view-empty view__grid">
          <Plug size={32} aria-hidden="true" />
          <h2>Plugin surface host</h2>
          <p className="view-tile__label">
            The isolated plugin tree for <strong>{pluginId}</strong> mounts here once its manifest is resolved. This container is scoped per plugin so a failing plugin cannot affect the workspace shell.
          </p>
          <Tag type="cool-gray">Sandboxed · manifest-driven</Tag>
        </Tile>
      )}
    </div>
  );
}
